import { useMemo } from "react";
import { useQuery } from "react-query";
import { useTranslation } from 'react-i18next';
import i18n from "i18next";
import { weightGValue, formatDate, formatNumber } from "@utils/formatValues";
import Skeleton from "@components/new/Skeleton";
import { H4, Title2, SubTitle3 } from "@components/new/Typo";
import Photo from "@components/new/Photo";
import { Marketplace } from "@components/new/Marketplace";
import { Modal, ModalSize } from "@components/new/Modal";
import { Table, TableCellLink, TableColumn } from "@components/new/Table";
import ToDisposal from "../../components/ToDisposal";
import {
  getWithdrawalDetails,
  getAliProductUrl,
  isWbRequestItem,
  isYmRequestItem,
  isAliRequestItem,
  TWithdrawalProductWb,
  TWithdrawalProductYm,
  TWithdrawalProductAli,
} from "@api/apiClient/fbp";
import CopyTextToClipboard from '@components/CopyTextToClipboard';
import WithdrwalStatusBadge from "../../components/WithdrwalStatusBadge";
import styles from "./Form.module.scss";

interface WithdrawalModalProps {
  onClose: () => void;
  id: string;
}

type TRow = (TWithdrawalProductWb | TWithdrawalProductYm | TWithdrawalProductAli) & {
  count?: number,
  marketplace: string;
};

const WithdrawalModal = ({ onClose, id }: WithdrawalModalProps) => {
  const { t } = useTranslation();
  const currentLanguage = i18n.language;

  const requestQuery = useQuery({
    queryKey: ["getWithdrawalDetails", id],
    queryFn: () => getWithdrawalDetails(id),
    retry: 1,
    select: (data) => {
      const items = [
        ...data.ymItems.map(v => ({
          ...v.ymProduct,
          marketplace: "Yandex",
          count: v.count,
        })),
        ...data.wbItems.map(v => ({
          ...v.wbProduct,
          marketplace: "Wildberries",
          count: v.count,
        })),
        ...(data.aliItems ?? []).map(v => ({
          ...v.aliProduct,
          marketplace: "Aliexpress",
          url: getAliProductUrl(v.aliProduct.aliItemId),
          count: v.count,
        })),
      ];
      return {
        ...data,
        items: items,
        weight: items.reduce((acc, v) => ((v.weight
          ? (weightGValue(v.weight) * v.count)
          : 0
        ) + acc), 0) / 1000,
        count: items.length,
      };
    }
  });

  const columns: TableColumn<TRow>[] = useMemo(() => [
    {
      header: t("fbpPage.withdrawalModal.items.photo"),
      renderCell: (row) => (
        <Photo src={row.data.primaryImage ?? ""} />
      ),
      width: "80px",
      fixWidth: true,
    },
    {
      header: t("fbpPage.withdrawalModal.items.marketplace"),
      renderCell: (row) => (
        <Marketplace name={row.data.marketplace} size="small" />
      ),
      alignment: "center",
      width: "40px",
      fixWidth: true,
    },
    {
      header: t("fbpPage.withdrawalModal.items.name"),
      renderCell: (row) => {
        const description = isYmRequestItem(row.data) ? row.data.sku : (
          isWbRequestItem(row.data) ? row.data.wbNmId : (
            isAliRequestItem(row.data) ? row.data.skuCode : ""
          )
        );
        return (
          <TableCellLink
            title={row.data.name}
            description={description}
            to={row.data.url}
            external
          />
        )
      },
      width: "200px",
    },
    {
      header: t("fbpPage.withdrawalModal.items.size"),
      valueGetter: (row) => ((isWbRequestItem(row) && row.wbTechSize) || ""),
      width: "160px",
      fixWidth: true,
      alignment: "center",
    },
    {
      header: t("fbpPage.withdrawalModal.items.count"),
      valueGetter: (data: TRow) => formatNumber(data.count ?? 0),
      width: "100px",
      fixWidth: true,
      alignment: "center",
    },
  ], [currentLanguage, requestQuery.data]);

  const skeletonColumns: TableColumn<null>[] = useMemo(() => columns.map(v => ({
    ...v,
    renderCell: undefined,
    valueGetter: undefined,
  })), [columns])

  return (
    <>
      <Modal
        isOpen={true}
        title={(<H4>{requestQuery.data?.number ?? ""}</H4>)}
        size={ModalSize.L}
        onClose={onClose}
      >
        <div className={styles.sectionRow}>
          <div className={styles.section}>
            <div className={styles.sectionColumn}>
              <h4>{t('fbpPage.withdrawalModal.params.title')}</h4>

              {requestQuery.isLoading ? (
                <div className={styles.params}>
                  <Skeleton height="36px" width="80px" />
                  <Skeleton height="36px" width="80px" />
                  <Skeleton height="36px" width="80px" />
                  <Skeleton height="36px" width="80px" />
                  <Skeleton height="36px" width="80px" />
                </div>
              ) : (
                <div className={styles.params}>
                  <div className={styles.paramsItem}>
                    <SubTitle3 color="grey-700">{t('fbpPage.withdrawalModal.params.count')}</SubTitle3>
                    <Title2>{requestQuery.data?.count}</Title2>
                  </div>

                  <div className={styles.paramsItem}>
                    <SubTitle3 color="grey-700">{t('fbpPage.withdrawalModal.params.weight')}</SubTitle3>
                    <Title2>{requestQuery.data?.weight ? `${formatNumber(requestQuery.data?.weight)} ${t('fbpPage.withdrawalModal.params.kg')}` : ""} </Title2>
                  </div>

                  <div className={styles.paramsItem}>
                    <SubTitle3 color="grey-700">{t('fbpPage.withdrawalModal.params.createdAt')}</SubTitle3>
                    <Title2>{formatDate(requestQuery.data?.createdAt, 'dd.MM.yyyy')}</Title2>
                  </div>

                  <div className={styles.paramsItem}>
                    <SubTitle3 color="grey-700">{t('fbpPage.withdrawalModal.params.status')}</SubTitle3>
                    <WithdrwalStatusBadge value={requestQuery.data?.status} />
                  </div>

                  {requestQuery.data?.disposalDate  ? (
                    <div className={styles.paramsItem}>
                      <SubTitle3 color="grey-700">{t('fbpPage.withdrawalModal.params.toUtilization')}</SubTitle3>
                        <ToDisposal
                          disposalDate={requestQuery.data?.disposalDate}
                          status={requestQuery.data?.status}
                        />
                    </div>
                  ) : null}
                </div>
              )}
            </div>
            {!["Completed", "Cancelled", "Destroyed"].find(v => (v === requestQuery.data?.status)) ? (
              <div className={styles.paramsRightItem}>
                <SubTitle3 color="grey-700">{t('fbpPage.withdrawalModal.params.trackNumber')}</SubTitle3>
                <CopyTextToClipboard
                  text={requestQuery.data?.number!}
                >
                  <Title2>{requestQuery.data?.number}</Title2>
                </CopyTextToClipboard>
              </div>
            ) : null}
          </div>
        </div>

        <div className={styles.section2}>
          <h4>{t('fbpPage.withdrawalModal.items.title')}</h4>

          {requestQuery.isLoading ? (
            <Table<null>
              isSkeleton
              columns={skeletonColumns}
              data={[null, null, null, null]}
            />
          ) : (
            <Table<TRow>
              columns={columns}
              data={requestQuery.data?.items ?? []}
            />
          )}
        </div>
      </Modal>
    </>
  )
}

export default WithdrawalModal;
